import React, { useState, useEffect, useRef } from 'react';
import { Modal, Form, Button, Row, Col, OverlayTrigger, Tooltip } from 'react-bootstrap';
import JoditEditor from 'jodit-react';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { getMenuList } from '../../services/menuService';
import './Menu.scss';

const MenuForm = ({ show, onHide, onSubmit, menuItem }) => {
  const editor = useRef(null);
  
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    isPage: false,
    dynamicPage: false,
    parentMenuId: null,
    displayOrder: 0,
    html: '',
    hasPopupNotification: false,
    popupContents: '',
    popupStartDate: new Date(),
    popupEndDate: new Date(new Date().setMonth(new Date().getMonth() + 1))
  });
  
  const [isEditable, setIsEditable] = useState(true);
  const [showHtmlEditor, setShowHtmlEditor] = useState(false);
  const [parentMenus, setParentMenus] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});

  // Üst menüleri getir
  useEffect(() => {
    const fetchParentMenus = async () => {
      try {
        const allMenus = await getMenuList();
        // isPage false olan menüleri filtrele (alt menü olabilecek olanlar)
        const potentialParents = allMenus.filter(menu => !menu.isPage);
        setParentMenus(potentialParents);
      } catch (error) {
        console.error('Error fetching parent menus:', error);
      }
    };
    
    fetchParentMenus();
  }, []);

  useEffect(() => {
    if (menuItem) {
      // Menü düzenlenebilir mi kontrol et
      const canEdit = menuItem.isPage && menuItem.dynamicPage;
      
      setIsEditable(canEdit);
      setFormData({
        ...menuItem,
        popupStartDate: menuItem.popupStartDate ? new Date(menuItem.popupStartDate) : new Date(),
        popupEndDate: menuItem.popupEndDate ? new Date(menuItem.popupEndDate) : new Date(new Date().setMonth(new Date().getMonth() + 1))
      });
      
      // Dinamik sayfa ise HTML editörünü göster
      setShowHtmlEditor(menuItem.isPage && menuItem.dynamicPage);
    } else {
      // Yeni menü ekleme durumu
      setIsEditable(true);
      setFormData({
        code: '',
        name: '',
        isPage: false,
        dynamicPage: false,
        parentMenuId: null,
        displayOrder: 0,
        html: '',
        hasPopupNotification: false,
        popupContents: '',
        popupStartDate: new Date(),
        popupEndDate: new Date(new Date().setMonth(new Date().getMonth() + 1))
      });
      setShowHtmlEditor(false);
    }
  }, [menuItem]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));
    
    // isPage veya dynamicPage değiştiğinde HTML editörünün görünürlüğünü güncelle
    if (name === 'isPage' || name === 'dynamicPage') {
      const newIsPage = name === 'isPage' ? newValue : formData.isPage;
      const newDynamicPage = name === 'dynamicPage' ? newValue : formData.dynamicPage;
      setShowHtmlEditor(newIsPage && newDynamicPage);
      
      // Eğer isPage false ise, dynamicPage de false olmalı
      if (name === 'isPage' && !newValue) {
        setFormData(prev => ({
          ...prev,
          [name]: newValue,
          dynamicPage: false
        }));
      }
    }
    
    // Validasyon hatalarını temizle
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validasyon kontrolleri
    const errors = {};
    
    if (!formData.code.trim()) {
      errors.code = 'Menü kodu zorunludur';
    }
    
    if (!formData.name.trim()) {
      errors.name = 'Menü adı zorunludur';
    }
    
    // isPage ve dynamicPage true ise html zorunlu
    if (formData.isPage && formData.dynamicPage && !formData.html.trim()) {
      errors.html = 'Dinamik sayfa için içerik zorunludur';
    }
    
    // Popup bilgileri kontrolü
    if (formData.hasPopupNotification) {
      if (!formData.popupContents.trim()) {
        errors.popupContents = 'Pop-up içeriği zorunludur';
      }
      
      if (!formData.popupStartDate) {
        errors.popupStartDate = 'Başlangıç tarihi zorunludur';
      }
      
      if (!formData.popupEndDate) {
        errors.popupEndDate = 'Bitiş tarihi zorunludur';
      }
      
      if (formData.popupStartDate && formData.popupEndDate && 
          formData.popupStartDate > formData.popupEndDate) {
        errors.popupEndDate = 'Bitiş tarihi başlangıç tarihinden sonra olmalıdır';
      }
    }
    
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    
    // URL formatını düzenle
    let formattedCode = formData.code;
    if (formData.isPage && !formattedCode.startsWith('/')) {
      formattedCode = '/' + formattedCode;
    }
    
    const submitData = {
      ...formData,
      code: formattedCode,
      // Pop-up bilgilerini doğru formatta gönder
      popupStartDate: formData.hasPopupNotification ? formData.popupStartDate.toISOString() : null,
      popupEndDate: formData.hasPopupNotification ? formData.popupEndDate.toISOString() : null,
      // Eğer popup aktif değilse içeriği boşalt
      popupContents: formData.hasPopupNotification ? formData.popupContents : ''
    };
    
    onSubmit(submitData);
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{menuItem ? 'Menü Düzenle' : 'Yeni Menü Ekle'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Menü Adı <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  isInvalid={!!validationErrors.name}
                />
                <Form.Control.Feedback type="invalid">
                  {validationErrors.name}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Menü Kodu <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  name="code"
                  value={formData.code}
                  onChange={handleChange}
                  placeholder="Örnek: test-link"
                  disabled={menuItem && !isEditable}
                  isInvalid={!!validationErrors.code}
                />
                <Form.Control.Feedback type="invalid">
                  {validationErrors.code}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  Benzersiz bir kod girin (örn: test-link)
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Görüntüleme Sırası</Form.Label>
                <Form.Control
                  type="number"
                  name="displayOrder"
                  value={formData.displayOrder}
                  onChange={handleChange}
                  min="0"
                />
              </Form.Group>
            </Col>
            
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Üst Menü</Form.Label>
                <Form.Select
                  name="parentMenuId"
                  value={formData.parentMenuId || ''}
                  onChange={handleChange}
                >
                  <option value="">Ana Menü (Yok)</option>
                  {parentMenus.map(menu => (
                    <option key={menu.guid} value={menu.guid}>
                      {menu.name}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">
                  Menünün hangi üst menü altında görüneceğini seçin
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip>Bu menü doğrudan bir içeriğe veya sayfaya yönlendirir</Tooltip>}
              >
                <Form.Check
                  type="checkbox"
                  label="Bu bir Sayfa Menüsü mü?"
                  name="isPage"
                  checked={formData.isPage}
                  onChange={handleChange}
                  id="isPage-checkbox"
                />
              </OverlayTrigger>
            </Col>
            
            <Col md={6}>
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip>Dinamik sayfalar için içerik editörü kullanılır</Tooltip>}
              >
                <Form.Check
                  type="checkbox"
                  label="Dinamik İçerikli Sayfa mı?"
                  name="dynamicPage"
                  checked={formData.dynamicPage}
                  onChange={handleChange}
                  disabled={!formData.isPage}
                  id="dynamicPage-checkbox"
                />
              </OverlayTrigger>
            </Col>
          </Row>

          {formData.isPage && formData.dynamicPage && (
            <Form.Group className="mb-3">
              <Form.Label>Sayfa İçeriği {formData.isPage && formData.dynamicPage && <span className="text-danger">*</span>}</Form.Label>
              <div className={validationErrors.html ? 'border border-danger rounded' : ''}>
                <JoditEditor
                  ref={editor}
                  value={formData.html}
                  config={{
                    readonly: false,
                    height: 500,
                    enableDragAndDropFileToEditor: true,
                    uploader: { insertImageAsBase64URI: true },
                    removeButtons: ['about'],
                    toolbarSticky: true,
                    toolbarStickyOffset: 0,
                    toolbar: [
                      'source', '|',
                      'bold', 'italic', 'underline', 'strikethrough', '|',
                      'font', 'fontsize', 'brush', 'paragraph', '|',
                      'table', 'link', 'image', '|',
                      'left', 'center', 'right', 'justify', '|',
                      'undo', 'redo', '|',
                      'hr', 'eraser', 'fullsize'
                    ],
                    askBeforePasteHTML: false,
                    askBeforePasteFromWord: false,
                    defaultActionOnPaste: 'insert_clear_html',
                    // Focus ve performans iyileştirmeleri
                    useSearch: false,
                    showCharsCounter: false,
                    showWordsCounter: false,
                    showXPathInStatusbar: false,
                    language: 'tr',
                    // Gereksiz yeniden render'ları önle
                    saveModeInStorage: false,
                    saveHeightInStorage: false
                  }}
                  onBlur={newContent => {
                    setFormData(prev => ({ ...prev, html: newContent }));
                    if (validationErrors.html) {
                      setValidationErrors(prev => ({ ...prev, html: null }));
                    }
                  }}

                />
              </div>
              {validationErrors.html && (
                <div className="text-danger mt-1">{validationErrors.html}</div>
              )}
            </Form.Group>
          )}

          <hr />
          
          {/* Pop-up Bildirim Ayarları */}
          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label="Pop-up Bildirim Gösterilsin mi?"
              name="hasPopupNotification"
              checked={formData.hasPopupNotification}
              onChange={handleChange}
              id="popup-checkbox"
            />
          </Form.Group>

          {formData.hasPopupNotification && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Pop-up İçeriği <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="popupContents"
                  value={formData.popupContents}
                  onChange={handleChange}
                  isInvalid={!!validationErrors.popupContents}
                />
                <Form.Control.Feedback type="invalid">
                  {validationErrors.popupContents}
                </Form.Control.Feedback>
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Başlangıç Tarihi <span className="text-danger">*</span></Form.Label>
                    <DatePicker
                      selected={formData.popupStartDate}
                      onChange={(date) => {
                        setFormData(prev => ({ ...prev, popupStartDate: date }));
                        if (validationErrors.popupStartDate) {
                          setValidationErrors(prev => ({ ...prev, popupStartDate: null }));
                        }
                      }}
                      showTimeSelect
                      dateFormat="dd/MM/yyyy HH:mm"
                      className={`form-control ${validationErrors.popupStartDate ? 'is-invalid' : ''}`}
                    />
                    {validationErrors.popupStartDate && (
                      <div className="invalid-feedback d-block">{validationErrors.popupStartDate}</div>
                    )}
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Bitiş Tarihi <span className="text-danger">*</span></Form.Label>
                    <DatePicker
                      selected={formData.popupEndDate}
                      onChange={(date) => {
                        setFormData(prev => ({ ...prev, popupEndDate: date }));
                        if (validationErrors.popupEndDate) {
                          setValidationErrors(prev => ({ ...prev, popupEndDate: null }));
                        }
                      }}
                      showTimeSelect
                      dateFormat="dd/MM/yyyy HH:mm"
                      className={`form-control ${validationErrors.popupEndDate ? 'is-invalid' : ''}`}
                      minDate={formData.popupStartDate}
                    />
                    {validationErrors.popupEndDate && (
                      <div className="invalid-feedback d-block">{validationErrors.popupEndDate}</div>
                    )}
                  </Form.Group>
                </Col>
              </Row>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            İptal
          </Button>
          <Button variant="primary" type="submit">
            {menuItem ? 'Güncelle' : 'Ekle'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default MenuForm;
